import { createStore } from 'react-sweet-state';
import { cloneDeep, isEqual, orderBy } from 'lodash';

import { pauseCOCampaign, startCOCampaign } from 'src/graphql/continuousOptimization';
import {
  queryChannelsAuthState,
  extractPerformanceCenterTextVariations,
  getPerformanceCenterResultDocument,
  queryProjectForNewCopies,
  performanceCenterUpdateConnectedAccounts,
  connectedAccounts,
  queryPerformanceCenterTextVariationsConnectedRequests,
  queryPerformanceCenterAdditionalData,
  createReviewsAccount,
  reviewsAccounts,
  websiteAccounts,
  createWebsiteAccount,
  unauthorizeChannel,
  performanceCenterRemoveConnectedAccounts,
  createResourceAccount,
  resourceAccounts,
  updateResourceAccount,
  queryPerformanceCenterInfoForDocument,
} from 'src/graphql/performance';
import { queryWTMProjectsForCIP } from 'src/graphql/project';
import {
  alignChannelFromBackend,
  alignChannelWithBackend,
  CHANNEL,
  alignChannelForUnauthorize,
} from 'src/data/channel';
import { isAsyncRequestError } from 'src/lib/errors';
import {
  TALKING_POINT_FIELD,
  CIP_ADS_PER_PAGE,
  REVIEWS_CHANNELS,
  WEBSITE_CHANNELS,
  RESOURCES,
  RESOURCE_DOCUMENTS,
  isFacebookChannel,
  isEmailCsvResourceDocument,
} from 'src/data/performance';
import { SORT_DIRECTION } from 'src/data/common';
import { isCampaignDraft, isCampaignRunning } from 'src/data/campaignStatus';
import { SOCIAL_AUTH_STATE } from 'src/data/user';
import {
  connectToChannel,
  isCopiesExtractionError,
  queryChannelAccounts,
  waitForCopiesExtraction,
} from './utils';

const NAME = 'performance-center-store';

const parsePerformance = (copy) => {
  try {
    return JSON.parse(copy.performance);
  } catch (e) {
    return {};
  }
};

const mapCopy = (copy) => ({
  ...copy,
  weightedCtr: copy.weightedAvgCtr,
  impressions: copy.lifetimeImpressions,
  ...parsePerformance(copy),
});
const prepareCopies = (copies) => copies.map(mapCopy);
const prepareAccountList = (accounts, selectedAccountIds = []) => {
  if (!accounts) {
    return [];
  }

  return accounts
    .map((account) => ({
      ...account,
      isConnected: selectedAccountIds?.includes(account.id),
    }))
    .sort((a, b) => b.isConnected - a.isConnected);
};

const INITIAL_STATE = {
  loadingAuthState: false,
  connectingToChannel: {},

  loadingCopies: {},
  extractingCopies: {},

  authState: {},
  authStateFetched: false,

  accounts: {},
  wtmProjects: [],

  selectedAccountIds: {},
  fetchedAccountIds: {},

  copies: {},

  lastExtractionDate: {},

  accountsSortField: null,
  accountsSortDirection: null,

  campaigns: {},

  clearCounter: 0,

  talkingPoints: {},

  avgCompletionPercent: {},
  extractionStatus: {},

  campaignsCopyImpressions: {},

  loadingConnectedAccounts: true,
  connectedAccountsFetched: false,
  creatingResource: false,
  loadingAccounts: false,
  loadingResourceAccounts: false,

  workspacesDataCache: {},

  authErrorMessage: {},

  deletedResourceAccounts: [],
  runningAccountIds: {},

  globalBenchmarkConfig: [],
  loadingGlobalBenchmark: false,

  connectedAccounts: {},
  warning: {},
};

const channelAbortControllers = new Map();

/**
 * Private actions
 */
export const PRIVATE_ACTIONS = {
  clearStore:
    (workspaceId) =>
    ({ getState, setState, dispatch }) => {
      dispatch(PRIVATE_ACTIONS.storeWorkspaceDataInCache(workspaceId));
      const { clearCounter, workspacesDataCache } = getState();

      setState({
        ...cloneDeep(INITIAL_STATE),
        workspacesDataCache,
        clearCounter,
      });
      dispatch(PRIVATE_ACTIONS.increaseClearCounter());
    },

  increaseClearCounter:
    () =>
    ({ getState, setState }) => {
      const { clearCounter } = getState();
      setState({ clearCounter: clearCounter + 1 });
    },

  storeWorkspaceDataInCache:
    (workspaceId) =>
    ({ getState, setState }) => {
      const {
        copies,
        accounts,
        selectedAccountIds,
        authState,
        authStateFetched,
        talkingPoints,
        lastExtractionDate,
        campaigns,
        campaignsCopyImpressions,
        connectedAccountsFetched,
        loadingConnectedAccounts,
        workspacesDataCache,
        connectingToChannel,
        globalBenchmarkConfig,
      } = getState();

      setState({
        workspacesDataCache: {
          ...workspacesDataCache,
          [workspaceId]: {
            copies,
            accounts,
            selectedAccountIds,
            authState,
            authStateFetched,
            talkingPoints,
            lastExtractionDate,
            campaigns,
            campaignsCopyImpressions,
            connectedAccountsFetched,
            loadingConnectedAccounts,
            connectingToChannel,
            globalBenchmarkConfig,
          },
        },
      });
    },

  initAccountList:
    (accounts, channel) =>
    ({ dispatch }) => {
      const sorted = orderBy(accounts, 'name', SORT_DIRECTION.asc);
      const channelSelectedAccountIds = dispatch(
        PRIVATE_ACTIONS.getChannelSelectedAccountIds(channel)
      );

      const preparedAccountList = prepareAccountList(sorted, channelSelectedAccountIds);

      dispatch(PRIVATE_ACTIONS.setChannelAccounts(channel, preparedAccountList));
    },

  initCopyList:
    (copies, channel) =>
    ({ dispatch }) => {
      if (!copies?.length) {
        return;
      }
      const preparedCopies = prepareCopies(copies || []);

      const sorted = orderBy(preparedCopies, 'sortIndex', SORT_DIRECTION.asc);

      dispatch(PRIVATE_ACTIONS.setChannelCopies(channel, sorted));
    },

  initTalkingPointList:
    (newTalkingPoints, channel) =>
    ({ getState, setState }) => {
      const { talkingPoints } = getState();

      const sorted = orderBy(newTalkingPoints, TALKING_POINT_FIELD.rating, SORT_DIRECTION.desc);

      setState({
        talkingPoints: { ...talkingPoints, [channel]: sorted },
      });
    },

  initResourceTalkingPointList:
    (additionalData, channel, accountId) =>
    ({ getState, setState }) => {
      const { talkingPoints } = getState();

      if (accountId) {
        const channelTalkingPoints = talkingPoints[channel] || [];

        const extractedTalkingPoints = additionalData.find(
          (data) => data.performanceCenterTalkingPointsPerResource[0]?.accountId === accountId
        )?.performanceCenterTalkingPointsPerResource;

        const talkingPointsExisting = channelTalkingPoints
          .map((c) => c.accountId)
          .includes(extractedTalkingPoints.accountId);

        let newList;

        if (talkingPointsExisting) {
          newList = channelTalkingPoints?.map((tp) =>
            tp.accountId === extractedTalkingPoints.id ? extractedTalkingPoints : tp
          );
        } else {
          newList = [...channelTalkingPoints, ...extractedTalkingPoints];
        }

        setState({
          talkingPoints: {
            ...talkingPoints,
            [channel]: newList,
          },
        });
      } else {
        setState({
          talkingPoints: {
            ...talkingPoints,
            [channel]: additionalData.map(
              (data) => data.performanceCenterTalkingPointsPerResource[0]
            ),
          },
        });
      }
    },

  initSelectedAccountIds:
    (accountIds, channel) =>
    ({ getState, setState }) => {
      const { selectedAccountIds } = getState();
      setState({ selectedAccountIds: { ...selectedAccountIds, [channel]: accountIds || [] } });
    },

  setAvgCompletionPercent:
    (newAvgCompletionPercent, channel) =>
    ({ getState, setState }) => {
      const { avgCompletionPercent } = getState();

      setState({
        avgCompletionPercent: { ...avgCompletionPercent, [channel]: newAvgCompletionPercent },
      });
    },

  setExtractionStatus:
    (newExtractionStatus, channel) =>
    ({ getState, setState }) => {
      const { extractionStatus } = getState();

      setState({
        extractionStatus: { ...extractionStatus, [channel]: newExtractionStatus },
      });
    },

  clearSelectedAccountIds:
    (channel) =>
    ({ dispatch }) => {
      if (isEmailCsvResourceDocument(channel)) {
        return;
      }

      const accounts = dispatch(PRIVATE_ACTIONS.getChannelAccounts(channel));

      dispatch(
        ACTIONS.updateConnectedAccounts({
          selectedAccountIds: [],
          channel,
        })
      );

      const preparedAccountList = prepareAccountList(accounts, []);

      dispatch(PRIVATE_ACTIONS.setChannelAccounts(channel, preparedAccountList));
    },

  initLastExtractionDate:
    (newLastExtractionDate, channel) =>
    ({ getState, setState }) => {
      const { lastExtractionDate } = getState();

      setState({ lastExtractionDate: { ...lastExtractionDate, [channel]: newLastExtractionDate } });
    },

  getChannelLastExtractionDate:
    (channel) =>
    ({ getState }) => {
      const { lastExtractionDate } = getState();

      return lastExtractionDate[channel];
    },

  initCampaigns:
    (newCampaigns, channel) =>
    ({ getState, setState }) => {
      const { campaigns } = getState();

      setState({
        campaigns: { ...campaigns, [channel]: newCampaigns || [] },
      });
    },

  getChannelCopies:
    (channel) =>
    ({ getState }) => {
      const { copies } = getState();

      return copies[channel] || [];
    },

  setChannelCopies:
    (channel, newCopies) =>
    ({ getState, setState }) => {
      const { copies } = getState();

      setState({ copies: { ...copies, [channel]: newCopies } });
    },

  setChannelCampaignsCopyImpressions:
    (newCampaignsCopyImpressions, channel) =>
    ({ getState, setState }) => {
      const { campaignsCopyImpressions } = getState();

      setState({
        campaignsCopyImpressions: {
          ...campaignsCopyImpressions,
          [channel]: newCampaignsCopyImpressions,
        },
      });
    },

  mergeChannelCopies:
    (channel, newCopies) =>
    ({ getState, setState }) => {
      const { copies } = getState();

      setState({ copies: { ...copies, [channel]: newCopies } });
    },

  getChannelAccounts:
    (channel) =>
    ({ getState }) => {
      const { accounts } = getState();

      return accounts[channel];
    },

  setChannelAccounts:
    (channel, newAccounts) =>
    ({ getState, setState }) => {
      const { accounts } = getState();

      setState({
        accounts: { ...accounts, [channel]: newAccounts },
      });
    },

  getChannelSelectedAccountIds:
    (channel) =>
    ({ getState }) => {
      const { selectedAccountIds } = getState();

      return selectedAccountIds[channel] || [];
    },

  setChannelSelectedAccountIds:
    (channel, newSelectedAccountIds) =>
    ({ getState, setState }) => {
      const { selectedAccountIds, copies, lastExtractionDate } = getState();

      // clear copies if new newSelectedAccountIds are not the same
      if (!isEqual(selectedAccountIds[channel], newSelectedAccountIds)) {
        setState({
          copies: { ...copies, [channel]: [] },
          lastExtractionDate: { ...lastExtractionDate, [channel]: null },
        });
      }

      setState({
        selectedAccountIds: { ...selectedAccountIds, [channel]: newSelectedAccountIds },
      });
    },

  setLoadingCopies:
    (channel, loading) =>
    ({ getState, setState }) => {
      const { loadingCopies } = getState();

      setState({
        loadingCopies: { ...loadingCopies, [channel]: loading },
      });
    },

  setExtractingCopies:
    (channel, extracting) =>
    ({ getState, setState }) => {
      const { extractingCopies } = getState();

      setState({
        extractingCopies: { ...extractingCopies, [channel]: extracting },
      });
    },

  setConnectingToChannel:
    (channel, connecting) =>
    ({ getState, setState }) => {
      const { connectingToChannel } = getState();

      setState({
        connectingToChannel: { ...connectingToChannel, [channel]: connecting },
      });
    },

  getChannelLoadingCopies:
    (channel) =>
    ({ getState }) => {
      const { loadingCopies } = getState();

      return loadingCopies[channel];
    },

  getChannelExtractingCopies:
    (channel) =>
    ({ getState }) => {
      const { extractingCopies } = getState();

      return extractingCopies[channel];
    },

  queryRestOfTextVariationsData:
    (channel, workspaceId) =>
    async ({ dispatch }) => {
      const alignedChannel = alignChannelWithBackend(channel);

      const result = await queryPerformanceCenterTextVariationsConnectedRequests(
        alignedChannel,
        workspaceId
      );

      channelAbortControllers.delete(channel);

      if (!result?.length) {
        return;
      }

      dispatch(PRIVATE_ACTIONS.initCopyList(result[0]?.copies, channel));
      dispatch(PRIVATE_ACTIONS.initSelectedAccountIds(result[0]?.accountIds, channel));
      dispatch(PRIVATE_ACTIONS.initCampaigns(result[0]?.campaigns, channel));
      dispatch(PRIVATE_ACTIONS.setFetchedAccountIds(result[0]?.accountIds, channel));
    },

  mergeToAccountList:
    (channel, account) =>
    ({ getState, setState }) => {
      const { accounts } = getState();

      setState({ accounts: { ...accounts, [channel]: [...(accounts[channel] || []), account] } });
    },

  addDeletedResourceAccount:
    (accountId) =>
    ({ getState, setState }) => {
      const { deletedResourceAccounts } = getState();

      setState({ deletedResourceAccounts: [...deletedResourceAccounts, accountId] });
    },

  setChannelAuthState:
    (channel, authState) =>
    ({ getState, setState }) => {
      const { authState: currentAuthState } = getState();

      let updatedAuthState;
      if (isFacebookChannel(channel)) {
        updatedAuthState = {
          ...currentAuthState,
          [CHANNEL.facebook]: authState,
          [CHANNEL.facebookPages]: authState,
          [CHANNEL.instagram]: authState,
        };
      } else {
        updatedAuthState = { ...currentAuthState, [channel]: authState };
      }

      setState({
        authState: updatedAuthState,
      });
    },

  setChannelWarning:
    (channel, warningText) =>
    ({ getState, setState }) => {
      const { warning } = getState();

      setState({ warning: { ...warning, [channel]: warningText } });
    },

  setChannelRunningAccounts:
    (channel, accountIds) =>
    ({ getState, setState }) => {
      const { runningAccountIds } = getState();

      setState({ runningAccountIds: { ...runningAccountIds, [channel]: accountIds } });
    },

  setFetchedAccountIds:
    (accountIds, channel) =>
    ({ getState, setState }) => {
      const { fetchedAccountIds } = getState();
      setState({ fetchedAccountIds: { ...fetchedAccountIds, [channel]: accountIds } });
    },

  setConnectedAccounts:
    (perChannelInfo, channel) =>
    ({ getState, setState }) => {
      const { connectedAccounts } = getState();
      setState({ connectedAccounts: { ...connectedAccounts, [channel]: perChannelInfo } });
    },
};

/**
 * Public available actions
 */
export const ACTIONS = {
  init:
    () =>
    ({ setState }) => {
      setState(cloneDeep(INITIAL_STATE));
    },

  queryChannelsAuthState:
    (force) =>
    async ({ getState, setState }) => {
      const { authStateFetched } = getState();
      if (authStateFetched && !force) {
        return;
      }

      setState({ loadingAuthState: true });
      try {
        const { googleAuthState, facebookAuthState, linkedinAuthState, hubspotAuthState } =
          await queryChannelsAuthState();

        const { authState } = getState();
        authState[CHANNEL.google] = googleAuthState;
        authState[CHANNEL.facebook] = facebookAuthState;
        authState[CHANNEL.linkedin] = linkedinAuthState;
        authState[CHANNEL.hubspot] = hubspotAuthState;
        authState[CHANNEL.facebookPages] = facebookAuthState;
        authState[CHANNEL.instagram] = facebookAuthState;

        setState({
          authState,
          authStateFetched: true,
        });
      } finally {
        setState({ loadingAuthState: false });
      }
    },

  connectToChannel:
    (channel) =>
    async ({ setState, dispatch }) => {
      dispatch(PRIVATE_ACTIONS.setConnectingToChannel(channel, true));

      try {
        const result = await connectToChannel(channel);
        dispatch(PRIVATE_ACTIONS.setChannelAuthState(channel, result?.authState));
        return result?.authState === SOCIAL_AUTH_STATE.authorized;
      } finally {
        dispatch(PRIVATE_ACTIONS.setConnectingToChannel(channel, false));
        setState({ loadingAccounts: false });
      }
    },

  isChannelConnected:
    (channel) =>
    ({ getState }) => {
      const { authState } = getState();
      return authState[channel] === SOCIAL_AUTH_STATE.authorized;
    },

  disconnectFromChannel:
    (channel) =>
    async ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.setConnectingToChannel(channel, true));

      const alignedChannel = alignChannelForUnauthorize(channel);
      try {
        await unauthorizeChannel(alignedChannel);

        dispatch(PRIVATE_ACTIONS.setChannelAuthState(channel, SOCIAL_AUTH_STATE.unauthorized));

        // remove channel accounts list
        dispatch(PRIVATE_ACTIONS.initAccountList([], channel));
      } finally {
        dispatch(PRIVATE_ACTIONS.setConnectingToChannel(channel, false));
      }
    },

  queryConnectedChannelAccounts:
    (channel) =>
    async ({ setState, dispatch }) => {
      setState({ loadingAccounts: true });
      try {
        const result = await queryChannelAccounts(channel);
        const accounts = result || [];
        dispatch(PRIVATE_ACTIONS.initAccountList(accounts, channel));
      } finally {
        setState({ loadingAccounts: false });
      }
    },

  queryReviewsAccounts:
    () =>
    async ({ dispatch, setState }, { notifications, currentWorkspaceId }) => {
      setState({ loadingResourceAccounts: true });
      try {
        const result = await reviewsAccounts(currentWorkspaceId);
        const accounts = result || [];
        REVIEWS_CHANNELS.forEach((channel) => {
          const filteredAccounts = accounts.filter((account) => account.siteType === channel);
          dispatch(PRIVATE_ACTIONS.initAccountList(filteredAccounts, channel));
        });
      } catch (error) {
        notifications.displayError(error);
      } finally {
        setState({ loadingResourceAccounts: false });
      }
    },

  queryWebsiteAccounts:
    () =>
    async ({ dispatch, setState }, { notifications, currentWorkspaceId }) => {
      setState({ loadingResourceAccounts: true });
      try {
        const result = await websiteAccounts(currentWorkspaceId);
        const accounts = result || [];
        WEBSITE_CHANNELS.forEach((channel) => {
          const filteredAccounts = accounts.filter((account) => account.websiteType === channel);
          dispatch(PRIVATE_ACTIONS.initAccountList(filteredAccounts, channel));
        });
      } catch (error) {
        notifications.displayError(error);
      } finally {
        setState({ loadingResourceAccounts: false });
      }
    },

  queryResourceAccounts:
    () =>
    async ({ dispatch, setState }, { notifications, currentWorkspaceId }) => {
      setState({ loadingResourceAccounts: true });
      try {
        const accounts = await resourceAccounts(currentWorkspaceId);

        RESOURCE_DOCUMENTS.forEach((resource) => {
          const filteredAccounts = accounts.filter((account) => account.dataSource === resource);
          dispatch(PRIVATE_ACTIONS.initAccountList(filteredAccounts, resource));
        });
      } catch (error) {
        notifications.displayError(error);
      } finally {
        setState({ loadingResourceAccounts: false });
      }
    },

  queryConnectedAccounts:
    (workspaceId) =>
    async ({ getState, dispatch, setState }, { notifications }) => {
      const { connectedAccountsFetched } = getState();
      if (connectedAccountsFetched) {
        return;
      }

      try {
        const results = await connectedAccounts(workspaceId);

        results?.forEach(({ channel, accountIds, perChannelInfo }) => {
          dispatch(
            PRIVATE_ACTIONS.initSelectedAccountIds(accountIds, alignChannelFromBackend(channel))
          );
          dispatch(
            PRIVATE_ACTIONS.setConnectedAccounts(perChannelInfo, alignChannelFromBackend(channel))
          );
        });

        setState({ connectedAccountsFetched: true, loadingConnectedAccounts: false });
      } catch (error) {
        notifications.displayError(error);
      }
    },

  createReviewsAccount:
    (urls, channel, onAfterExtraction) =>
    async ({ setState, dispatch }, { currentWorkspaceId }) => {
      setState({ creatingResource: true });

      try {
        const result = await createReviewsAccount(
          {
            workspaceId: currentWorkspaceId,
            urls,
            siteType: channel,
          },
          {
            abortSignal: channelAbortControllers.get(channel)?.signal,
          }
        );
        dispatch(PRIVATE_ACTIONS.mergeToAccountList(channel, result));
        dispatch(
          ACTIONS.extractTextVariations({
            channel,
            selectedAccountIds: [result.id],
            onAfterExtraction,
          })
        );

        return result;
      } finally {
        setState({ creatingResource: false });
      }
    },

  createWebsiteAccount:
    (urls, channel, onAfterExtraction) =>
    async ({ setState, dispatch }, { currentWorkspaceId }) => {
      setState({ creatingResource: true });

      try {
        const result = await createWebsiteAccount(
          {
            workspaceId: currentWorkspaceId,
            websiteType: channel,
            urls,
          },
          {
            abortSignal: channelAbortControllers.get(channel)?.signal,
          }
        );
        dispatch(PRIVATE_ACTIONS.mergeToAccountList(channel, result));
        dispatch(
          ACTIONS.extractTextVariations({
            channel,
            selectedAccountIds: [result.id],
            onAfterExtraction,
          })
        );
        return result;
      } finally {
        setState({ creatingResource: false });
      }
    },

  createResourceAccount:
    (file, channel, onAfterExtraction) =>
    async ({ setState, dispatch }, { currentWorkspaceId }) => {
      setState({ creatingResource: true });

      try {
        const result = await createResourceAccount(
          {
            workspaceId: currentWorkspaceId,
            name: file?.name.replace('.csv', ' (.csv)'),
            file,
            dataSource: channel,
          },
          {
            abortSignal: channelAbortControllers.get(channel)?.signal,
          }
        );
        dispatch(PRIVATE_ACTIONS.mergeToAccountList(channel, result));
        dispatch(
          ACTIONS.extractTextVariations({
            channel,
            selectedAccountIds: [result.id],
            onAfterExtraction,
            skipConnectedAccounts: true,
          })
        );

        return result;
      } finally {
        setState({ creatingResource: false });
      }
    },

  deleteDocumentResource:
    (channel, id) =>
    async ({ getState, setState, dispatch }, { currentWorkspaceId, notifications }) => {
      const { accounts } = getState();
      try {
        await updateResourceAccount({
          id,
          workspaceId: currentWorkspaceId,
          connected: false,
        });
        const newAccounts = accounts[channel].filter((account) => account.id !== id);

        dispatch(PRIVATE_ACTIONS.setChannelAccounts(channel, newAccounts));
        dispatch(PRIVATE_ACTIONS.addDeletedResourceAccount(id));
      } catch (error) {
        notifications.displayError(error);
      }
    },
  setCreatingResource:
    (creatingResource) =>
    ({ setState }) => {
      setState({ creatingResource });
    },

  setLoadingConnectedAccounts:
    (loadingConnectedAccounts) =>
    ({ setState }) => {
      setState({ loadingConnectedAccounts });
    },

  extractTextVariations:
    ({ channel, selectedAccountIds, onAfterExtraction, skipConnectedAccounts }) =>
    async ({ dispatch, getState }, { notifications, currentWorkspaceId }) => {
      const { selectedAccountIds: currentSelectedAccountIds } = getState();

      if (RESOURCES.includes(channel)) {
        dispatch(ACTIONS.setAccountExtractingCopies(channel, selectedAccountIds[0], true));
      } else {
        dispatch(PRIVATE_ACTIONS.setExtractingCopies(channel, true));
      }

      const alignedChannel = alignChannelWithBackend(channel);

      channelAbortControllers.set(channel, new AbortController());
      const setRunningAccountIds = (channel, accountIds) => {
        dispatch(PRIVATE_ACTIONS.setChannelRunningAccounts(channel, accountIds));
      };
      try {
        await extractPerformanceCenterTextVariations(
          {
            accountIds: selectedAccountIds,
            workspaceId: currentWorkspaceId,
            assetType: null,
            dataSource: alignedChannel,
            insightsIntervalDays: null,
          },
          {
            abortSignal: channelAbortControllers.get(channel)?.signal,
          }
        );

        if (!skipConnectedAccounts) {
          dispatch(PRIVATE_ACTIONS.setChannelSelectedAccountIds(channel, selectedAccountIds));
        }

        const setAvgCompletionPercent = (avgCompletionPercent, channel) => {
          dispatch(PRIVATE_ACTIONS.setAvgCompletionPercent(avgCompletionPercent, channel));
        };

        const deleteResource = (channel, failedResourceAccountId) => {
          if (RESOURCE_DOCUMENTS.includes(channel)) {
            dispatch(ACTIONS.deleteDocumentResource(channel, failedResourceAccountId));
          } else {
            dispatch(ACTIONS.deleteResource(channel, failedResourceAccountId));
          }

          dispatch(ACTIONS.cancelWaitRequests(channel));
        };

        const setExtractionStatus = (extractionStatus, channel) => {
          dispatch(PRIVATE_ACTIONS.setExtractionStatus(extractionStatus, channel));
        };

        setAvgCompletionPercent(0, channel);

        const result = await waitForCopiesExtraction({
          channel: alignedChannel,
          workspaceId: currentWorkspaceId,
          notifications,
          options: {
            abortSignal: channelAbortControllers.get(channel)?.signal,
          },
          setAvgCompletionPercent,
          textVariationsResultsCount: CIP_ADS_PER_PAGE,
          deleteResource,
          setChannelWarning: PRIVATE_ACTIONS.setChannelWarning,
          setRunningAccountIds,
          setExtractionStatus,
        });

        setRunningAccountIds(channel, []);

        if (onAfterExtraction) {
          await onAfterExtraction();
        }

        channelAbortControllers.delete(channel);

        if (!result?.length) {
          return;
        }

        const updatedAccounts = [
          ...(currentSelectedAccountIds[channel] || []),
          ...(result[0]?.accountIds || []),
        ];
        dispatch(PRIVATE_ACTIONS.initCopyList(result[0]?.copies, channel));
        dispatch(PRIVATE_ACTIONS.initSelectedAccountIds(updatedAccounts, channel));
        dispatch(PRIVATE_ACTIONS.initLastExtractionDate(result[0]?.updatedAt, channel));
        dispatch(PRIVATE_ACTIONS.initCampaigns(result[0]?.campaigns, channel));

        const additionalData = await queryPerformanceCenterAdditionalData(
          alignedChannel,
          currentWorkspaceId
        );

        if (RESOURCES.includes(channel)) {
          dispatch(
            PRIVATE_ACTIONS.initResourceTalkingPointList(
              additionalData,
              channel,
              selectedAccountIds[0]
            )
          );
        } else {
          dispatch(
            PRIVATE_ACTIONS.initTalkingPointList(
              additionalData[0]?.performanceCenterTalkingPointsPerResource,
              channel
            )
          );
        }

        dispatch(
          PRIVATE_ACTIONS.setChannelCampaignsCopyImpressions(
            additionalData[0]?.campaignsCopyImpressions,
            channel
          )
        );

        await dispatch(PRIVATE_ACTIONS.queryRestOfTextVariationsData(channel, currentWorkspaceId));
      } catch (error) {
        dispatch(PRIVATE_ACTIONS.initLastExtractionDate(null, channel));
        if (isAsyncRequestError(error)) {
          dispatch(ACTIONS.queryChannelsAuthState());
          dispatch(PRIVATE_ACTIONS.clearSelectedAccountIds(channel));
        }
        if (isCopiesExtractionError(error)) {
          dispatch(ACTIONS.queryChannelsAuthState(true));
          dispatch(ACTIONS.setAuthErrorMessage(channel, error.message));
        }
        if (error.name !== 'AbortError') {
          notifications.displayError(error);
        }
        setRunningAccountIds(channel, []);
        dispatch(PRIVATE_ACTIONS.setExtractionStatus(null, channel));
      } finally {
        if (RESOURCES.includes(channel)) {
          dispatch(ACTIONS.setAccountExtractingCopies(channel, selectedAccountIds[0], false));
        } else {
          dispatch(PRIVATE_ACTIONS.setExtractingCopies(channel, false));
        }
        dispatch(PRIVATE_ACTIONS.setLoadingCopies(channel, false));
        dispatch(PRIVATE_ACTIONS.setAvgCompletionPercent(null, channel));
      }
    },

  updateConnectedAccounts:
    ({ channel, selectedAccountIds }) =>
    async ({ dispatch }, { notifications, currentWorkspaceId }) => {
      try {
        let newAccountIdsList = selectedAccountIds;

        if (RESOURCES.includes(channel)) {
          // in case of resource channel we use the account ids and not the connected ones
          const channelAccounts = dispatch(PRIVATE_ACTIONS.getChannelAccounts(channel));
          newAccountIdsList = channelAccounts.map((account) => account.id);
        }
        await performanceCenterUpdateConnectedAccounts({
          workspaceId: currentWorkspaceId,
          accountIds: newAccountIdsList,
          channel: alignChannelWithBackend(channel),
        });
        dispatch(PRIVATE_ACTIONS.setChannelSelectedAccountIds(channel, newAccountIdsList));
      } catch (error) {
        notifications.displayError(error);
      }
    },

  deleteResource:
    (channel, accountId) =>
    async ({ getState, dispatch, notifications }, { currentWorkspaceId }) => {
      const { accounts } = getState();

      const channelAccounts = accounts[channel];

      const newAccounts = channelAccounts.filter((account) => account.id !== accountId);
      try {
        await performanceCenterRemoveConnectedAccounts({
          workspaceId: currentWorkspaceId,
          accountIds: [accountId],
          channel: alignChannelWithBackend(channel),
        });

        dispatch(PRIVATE_ACTIONS.setChannelAccounts(channel, newAccounts));
        dispatch(PRIVATE_ACTIONS.addDeletedResourceAccount(accountId));
      } catch (error) {
        notifications.displayError(error);
      }
    },

  getChannelLastExtractionDate: PRIVATE_ACTIONS.getChannelLastExtractionDate,
  setExtractingCopies: PRIVATE_ACTIONS.setExtractingCopies,
  getChannelExtractingCopies: PRIVATE_ACTIONS.getChannelExtractingCopies,
  initSelectedAccountIds: PRIVATE_ACTIONS.initSelectedAccountIds,
  initLastExtractionDate: PRIVATE_ACTIONS.initLastExtractionDate,

  getChannelLastExtraction:
    (channel) =>
    async ({ dispatch }, { notifications, currentWorkspaceId }) => {
      const alignedChannel = alignChannelWithBackend(channel);

      const lastExtractionDate = dispatch(PRIVATE_ACTIONS.getChannelLastExtractionDate(channel));

      const loadingCopies = dispatch(PRIVATE_ACTIONS.getChannelLoadingCopies(channel));
      const extractingCopies = dispatch(PRIVATE_ACTIONS.getChannelExtractingCopies(channel));

      dispatch(ACTIONS.setLoadingConnectedAccounts(false));

      // don't fetch copies again if already did on same session
      if ((lastExtractionDate && !extractingCopies) || loadingCopies) {
        return;
      }

      dispatch(PRIVATE_ACTIONS.setLoadingCopies(channel, true));

      channelAbortControllers.set(channel, new AbortController());

      const setAvgCompletionPercent = (avgCompletionPercent, channel) => {
        dispatch(PRIVATE_ACTIONS.setAvgCompletionPercent(avgCompletionPercent, channel));
      };

      const setRunningAccountIds = (channel, accountIds) => {
        dispatch(PRIVATE_ACTIONS.setChannelRunningAccounts(channel, accountIds));
      };

      const deleteResource = (channel, failedResourceAccountId) => {
        if (RESOURCE_DOCUMENTS.includes(channel)) {
          dispatch(ACTIONS.deleteDocumentResource(channel, failedResourceAccountId));
        } else {
          dispatch(ACTIONS.deleteResource(channel, failedResourceAccountId));
        }
      };

      const setExtractionStatus = (extractionStatus, channel) => {
        dispatch(PRIVATE_ACTIONS.setExtractionStatus(extractionStatus, channel));
      };

      try {
        const result = await waitForCopiesExtraction({
          channel: alignedChannel,
          workspaceId: currentWorkspaceId,
          notifications,
          options: {
            abortSignal: channelAbortControllers.get(channel)?.signal,
          },
          setAvgCompletionPercent,
          textVariationsResultsCount: CIP_ADS_PER_PAGE,
          deleteResource,
          setChannelWarning: PRIVATE_ACTIONS.setChannelWarning,
          setRunningAccountIds,
          setExtractionStatus,
        });
        channelAbortControllers.delete(channel);
        setRunningAccountIds(channel, []);

        if (!result?.length) {
          return;
        }

        dispatch(PRIVATE_ACTIONS.initCopyList(result[0]?.copies, channel));
        dispatch(PRIVATE_ACTIONS.initSelectedAccountIds(result[0]?.accountIds, channel));
        dispatch(PRIVATE_ACTIONS.initLastExtractionDate(result[0]?.updatedAt, channel));
        dispatch(PRIVATE_ACTIONS.initCampaigns(result[0]?.campaigns, channel));

        const additionalData = await queryPerformanceCenterAdditionalData(
          alignedChannel,
          currentWorkspaceId
        );

        if (RESOURCES.includes(channel)) {
          dispatch(PRIVATE_ACTIONS.initResourceTalkingPointList(additionalData, channel));
        } else {
          dispatch(
            PRIVATE_ACTIONS.initTalkingPointList(
              additionalData[0]?.performanceCenterTalkingPointsPerResource,
              channel
            )
          );
        }

        dispatch(
          PRIVATE_ACTIONS.setChannelCampaignsCopyImpressions(
            additionalData[0]?.campaignsCopyImpressions,
            channel
          )
        );

        if (RESOURCE_DOCUMENTS.includes(channel)) {
          dispatch(ACTIONS.queryResourceAccounts());
        }

        await dispatch(PRIVATE_ACTIONS.queryRestOfTextVariationsData(channel, currentWorkspaceId));
      } catch (error) {
        dispatch(PRIVATE_ACTIONS.initLastExtractionDate(null, channel));
        if (isAsyncRequestError(error)) {
          dispatch(ACTIONS.queryChannelsAuthState());
          dispatch(PRIVATE_ACTIONS.clearSelectedAccountIds(channel));
          if (RESOURCES.includes(channel)) {
            const failedResourceAccountId = dispatch(PRIVATE_ACTIONS.getFailedResourceAccountId());
            dispatch(ACTIONS.deleteResource(channel, failedResourceAccountId));
            notifications.displayError(error);
          }
        }
        if (isCopiesExtractionError(error)) {
          dispatch(ACTIONS.queryChannelsAuthState(true));
          dispatch(ACTIONS.setAuthErrorMessage(channel, error.message));
        }
        if (error.name !== 'AbortError') {
          throw error;
        }
        dispatch(PRIVATE_ACTIONS.setExtractionStatus(null, channel));
      } finally {
        dispatch(PRIVATE_ACTIONS.setLoadingCopies(channel, false));
        dispatch(PRIVATE_ACTIONS.setExtractingCopies(channel, false));
        dispatch(PRIVATE_ACTIONS.setAvgCompletionPercent(null, channel));
      }
    },

  sortAccounts:
    (field, channel) =>
    ({ getState, setState, dispatch }) => {
      const accounts = dispatch(PRIVATE_ACTIONS.getChannelAccounts(channel));
      const { accountsSortField, accountsSortDirection } = getState();

      const sameField = accountsSortField === field;
      const sortedAsc = accountsSortDirection === SORT_DIRECTION.asc;
      const newDirection = sameField && sortedAsc ? SORT_DIRECTION.desc : SORT_DIRECTION.asc;

      const sorted = orderBy(accounts, [field], [newDirection]);

      dispatch(PRIVATE_ACTIONS.setChannelAccounts(channel, sorted));

      setState({
        accountsSortField: field,
        accountsSortDirection: newDirection,
      });
    },

  getPerformanceCenterResultDocument:
    (adId) =>
    async (_, { notifications, currentCustomerId }) => {
      try {
        const projectId = await getPerformanceCenterResultDocument(adId, currentCustomerId);

        return projectId;
      } catch (error) {
        notifications.displayError(error);
      }
    },

  getChannelSelectedAccountIds: PRIVATE_ACTIONS.getChannelSelectedAccountIds,

  setAccountExtractingCopies:
    (channel, accountId, extracting) =>
    ({ getState, setState }) => {
      const { extractingCopies } = getState();

      setState({
        extractingCopies: {
          ...extractingCopies,
          [channel]: { ...extractingCopies[channel], [accountId]: extracting },
        },
      });
    },

  getProjectForNewCopies:
    (channel) =>
    async (_, { currentWorkspaceId }) => {
      const alignedChannel = alignChannelWithBackend(channel);

      const result = await queryProjectForNewCopies(alignedChannel, currentWorkspaceId);

      return result?.projectForNewCopies;
    },

  clearStore: PRIVATE_ACTIONS.clearStore,

  cancelWaitRequests: (channel) => () => {
    if (channel) {
      if (channelAbortControllers.has(channel)) {
        channelAbortControllers.get(channel)?.abort();
        channelAbortControllers.delete(channel);
      }
    } else {
      channelAbortControllers.forEach((channelAbortController) => {
        channelAbortController.abort();
      });
      channelAbortControllers.clear();
    }
  },

  restoreWorkspaceDataFromCache:
    (workspaceId) =>
    ({ getState, setState }) => {
      const { workspacesDataCache } = getState();

      if (workspacesDataCache[workspaceId]) {
        setState(workspacesDataCache[workspaceId]);
      }
    },

  setAuthErrorMessage:
    (channel, errorMessage) =>
    ({ getState, setState }) => {
      const { authErrorMessage } = getState();

      setState({
        authErrorMessage: {
          ...authErrorMessage,
          [channel]: errorMessage,
        },
      });
    },
  clearAuthErrorMessage:
    (channel) =>
    ({ getState, setState }) => {
      const { authErrorMessage } = getState();

      delete authErrorMessage[channel];

      setState({
        authErrorMessage,
      });
    },

  getChannelAuthState:
    (channel) =>
    ({ getState }) => {
      return getState().authState[channel];
    },

  queryPerformanceCenterInfoForDocument:
    () =>
    async ({ setState }, { currentWorkspaceId }) => {
      setState({ loadingGlobalBenchmark: true });
      try {
        const globalBenchmarkConfig = await queryPerformanceCenterInfoForDocument(
          currentWorkspaceId
        );

        setState({ globalBenchmarkConfig });
      } finally {
        setState({ loadingGlobalBenchmark: false });
      }
    },

  /**
   * Refresh WTM integration info: "auth state" and "active projects list"
   * @param {string} workspaceId - workspace id
   * @return {Promise}
   */
  refreshWTMIntegrationInfo:
    (workspaceId) =>
    async ({ setState, dispatch }, { currentCustomerId, currentWorkspaceId }) => {
      try {
        const result = await queryWTMProjectsForCIP({
          customerId: currentCustomerId,
          workspaceId: workspaceId || currentWorkspaceId,
        });
        const withNonDraftCampaign = result.filter(
          (project) => !isCampaignDraft(project?.continuousOptimizationCampaign?.status)
        );

        // Store WTM projects to display on "manage integration" popup
        setState({ wtmProjects: withNonDraftCampaign });

        // Store "WTM project" auth state
        const authState =
          withNonDraftCampaign.length > 0
            ? SOCIAL_AUTH_STATE.authorized
            : SOCIAL_AUTH_STATE.unauthorized;
        dispatch(PRIVATE_ACTIONS.setChannelAuthState(CHANNEL.projectWtm, authState));
      } catch (error) {
        // In case of an error - set auth state to "unauthorized"
        dispatch(
          PRIVATE_ACTIONS.setChannelAuthState(CHANNEL.projectWtm, SOCIAL_AUTH_STATE.unauthorized)
        );
      }
    },

  /**
   * Start/Pause whole WTM campaign and get the new "status"
   * @param {string} projectId - project id
   * @return {Promise<CAMPAIGN_STATUS>} - new campaign status
   */
  toggleWTMIntegrationStatus:
    (projectId) =>
    async ({ getState, setState }) => {
      // Get project and campaign from stored list
      const { wtmProjects } = getState();
      const project = wtmProjects.find((item) => item.id === projectId);
      const campaign = project?.continuousOptimizationCampaign;
      if (!campaign) {
        throw new Error('WTM campaign not found');
      }

      // Start or pause campaign and get the new status
      const result = isCampaignRunning(campaign.status)
        ? await pauseCOCampaign(campaign.id)
        : await startCOCampaign(campaign.id);

      // Rewrite status in stored projects
      const newList = wtmProjects.map((item) => {
        if (item.id === projectId) {
          return {
            ...item,
            continuousOptimizationCampaign: {
              ...item.continuousOptimizationCampaign,
              status: result.status,
            },
          };
        }
        return item;
      });
      setState({ wtmProjects: newList });

      // Return new status
      return result.status;
    },

  removeWTMIntegrationProject:
    (projectId) =>
    ({ getState, setState }) => {
      const { wtmProjects } = getState();
      const newList = wtmProjects.filter((item) => item.id !== projectId);
      setState({ wtmProjects: newList });
    },

  getSelectedAccountIds:
    () =>
    ({ getState }) => {
      return getState().selectedAccountIds;
    },

  getFirstConnectedChannel:
    () =>
    ({ getState }) => {
      const { authState, selectedAccountIds } = getState();

      const connectedChannels = Object.keys(authState)
        .filter((k) => authState[k] === SOCIAL_AUTH_STATE.authorized)
        .sort((k1, k2) => {
          // sort the list by the connected accounts in descending order
          return selectedAccountIds?.[k2]?.length || 0 - selectedAccountIds?.[k1]?.length || 0;
        });
      return connectedChannels?.[0];
    },

  refreshConnectedAccounts:
    () =>
    async ({ setState, dispatch }, { notifications, currentWorkspaceId }) => {
      try {
        const results = await connectedAccounts(currentWorkspaceId);

        if (!results.length) {
          setState({ connectedAccounts: {} });
          return;
        }

        results?.forEach(({ channel, perChannelInfo }) => {
          dispatch(
            PRIVATE_ACTIONS.setConnectedAccounts(perChannelInfo, alignChannelFromBackend(channel))
          );
        });
      } catch (error) {
        notifications.displayError(error);
      }
    },
};

export const PerformanceStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: NAME,
});
